import { email, getTeamClaims } from '@/firebase'
import { switchTeam } from '@/helpers/apiClient'
import { Team } from '@/helpers/fs/team'
import { TeamMember } from '@/helpers/fs/teams/teamMember'
import { setQuery } from '@/helpers/hasQuery'
import { getDocs, onSnapshot } from 'firebase/firestore'
import { defineStore } from 'pinia'

type State = {
  teams: { [teamId: string]: Team }
  invitation: Invitation | null
  teamIds: string[]
  currentTeamId: string | null
}

export const useTeamStore = defineStore('team', {
  state: (): State => {
    return {
      teams: {},
      invitation: null,
      teamIds: [],
      currentTeamId: null
    }
  },
  actions: {
    async setup() {
      // NOTE: Authにアクセスしすぎると簡単にlimitエラーになるので、ここで1度だけ初期化
      const teamClaim = await getTeamClaims()
      this.$patch(teamClaim)

      await this.setupTeams()
      this.checkInvitation()
    },
    // チームのセットアップ
    async setupTeams() {
      const teamDocs = await Team.userBelongingsTeamDocs()
      await Promise.all(
        teamDocs.map(async (t) => {
          // 初期化
          const team = await Team.build(t.id)
          this.$patch({ teams: { [t.id]: team } })

          // 同期
          onSnapshot(team.ref, async (ds) => {
            // 削除した時に転けるので
            if (!ds.exists()) return
            const t = await Team.build(ds.ref.id)
            this.$patch({ teams: { [t.id]: t } })
          })

          // チームメンバーに対する同期
          onSnapshot(TeamMember.collection(team.id), async (qs) => {
            const t = await Team.build(team.id)
            this.$patch({ teams: { [t.id]: t } })
          })
        })
      )
    },
    // 招待
    async checkInvitation() {
      const query = TeamMember.membersQuery(email()!, false)
      const qs = await getDocs(query)
      // NOTE: 早くモーダルが非表示になってしまったりで、逆に不便だったのでonSnapshotはやめた
      if (qs.docs.length > 0) {
        const memberDoc = qs.docs[0]
        const teamDoc = Team.docFromMember(memberDoc.ref)
        const team = await Team.build(teamDoc.id)
        const member = team.currentMember!
        const invitation: Invitation = {
          team,
          member
        }
        this.$patch({ invitation })
      } else {
        this.$patch({ invitation: null })
      }
    },
    async switchTeam(teamId: string) {
      await switchTeam(teamId)
      setQuery('message', `チームを${this.teams[teamId].name}切り替えました。`)
      // 大人しく画面をリロード
      window.location.reload()
    }
  },
  getters: {
    currentTeam(): Team | null {
      if (this.currentTeamId === null) return null
      return this.teams[this.currentTeamId]
    },
    currentTeamMember(): TeamMember | null {
      return this.currentTeam?.currentMember ?? null
    },
    teamsArray(): Team[] {
      return Object.values(this.teams)
    },
    teamCount(): number {
      return this.teamsArray.length
    },
    belongsToTeam(): boolean {
      return this.teamCount > 0
    },
    teamMembers(state): TeamMember[] {
      return this.currentTeam?.members ?? []
    },
    approvedTeamMembers(state): TeamMember[] {
      return this.teamMembers.filter((m) => m.approved)
    },
    teamSettingLinkPath(): string {
      if (!this.belongsToTeam) return '/settings/team/create'
      return `/settings/team`
    },
    useMediatory(): boolean {
      return this.currentTeam?.useMediatory ?? false
    },
    isPortersAuthed(): boolean {
      return this.currentTeam?.isAuthorized('porters') ?? false
    }
  }
})

type Invitation = {
  team: Team
  member: TeamMember
}
